import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Lock, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { PhoneField } from '@/components/PhoneField';
import { FormField } from '@/components/FormField';
import { Modal } from '@/components/Modal';
import { StatusPill } from '@/components/StatusPill';
import { SettingsCard, SettingsPage, SettingsRow, SettingsSection } from '@/components/settings/SettingsPage';
import { settingsService, MeProfile } from '@/services/settings.service';
import { TwoFactorCard } from '@/components/settings/TwoFactorCard';

export default function ProfileSettings() {
  const [me, setMe] = useState<MeProfile | null>(null);
  const [saved, setSaved] = useState<MeProfile | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  const load = () => {
    setFailed(false);
    settingsService.me().then((r) => { setMe(r); setSaved(r); }).catch(() => setFailed(true));
  };
  useEffect(load, []);

  const dirty = !!me && !!saved
    && (me.firstName !== saved.firstName || me.lastName !== saved.lastName || (me.phone ?? '') !== (saved.phone ?? ''));

  const save = async () => {
    if (!me) return;
    setBusy(true);
    try {
      const updated = await settingsService.updateMe({
        firstName: me.firstName,
        lastName: me.lastName,
        phone: me.phone,
      });
      setMe(updated);
      setSaved(updated);
      toast.success('Profile saved');
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (newPw !== confirmPw) {
      toast.error('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await settingsService.changePassword({ currentPassword: currentPw, newPassword: newPw });
      toast.success('Password changed');
      setPwOpen(false);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch {
      /* toast shown by interceptor */
    } finally {
      setBusy(false);
    }
  };

  const header = { eyebrow: 'Account', title: 'My profile', description: 'How you appear to your teammates, and how you sign in.' };

  if (!me) {
    return (
      <SettingsPage {...header}>
        {failed ? (
          <div className="py-7">
            <SettingsCard><p className="text-sm text-text-secondary">Your profile couldn't be loaded. <button type="button" onClick={load} className="font-medium text-primary hover:underline">Try again</button></p></SettingsCard>
          </div>
        ) : (
          <div className="space-y-4 py-7"><div className="skeleton h-28 rounded-2xl" /><div className="skeleton h-64 rounded-2xl" /></div>
        )}
      </SettingsPage>
    );
  }

  const initials = `${me.firstName.charAt(0)}${me.lastName.charAt(0)}`.toUpperCase();
  const lastLogin = me.lastLoginAt ? new Date(me.lastLoginAt).toLocaleString() : 'Never';

  return (
    <SettingsPage {...header}>
      <div className="py-6">
        <SettingsCard>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gold-gradient-soft font-display text-xl font-bold text-primary shadow-soft">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="truncate font-display text-lg font-bold tracking-tight text-text-primary">{me.firstName} {me.lastName}</h2>
                <StatusPill status={me.role.replace('company_', '')} variant={me.role === 'company_admin' ? 'info' : 'neutral'} />
                <StatusPill status={me.status} />
              </div>
              <div className="mt-1 truncate font-mono text-xs text-text-muted">{me.email}</div>
            </div>
            <div className="shrink-0 sm:text-right">
              <div className="text-2xs uppercase tracking-wide text-text-muted">Last sign-in</div>
              <div className="text-xs text-text-secondary">{lastLogin}</div>
            </div>
          </div>
        </SettingsCard>
      </div>

      <SettingsSection title="Personal details" description="Your name shows on the leads you own, on notes and in the activity log.">
        <SettingsCard
          footer={dirty ? (
            <>
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={() => saved && setMe(saved)}>Discard</Button>
              <Button size="sm" iconLeft={<Save size={14} />} loading={busy} disabled={!me.firstName.trim()} onClick={save}>Save changes</Button>
            </>
          ) : undefined}
        >
          <div className="grid gap-x-4 sm:grid-cols-2">
            <FormField label="First name">
              <Input value={me.firstName} onChange={(e) => setMe({ ...me, firstName: e.target.value })} />
            </FormField>
            <FormField label="Last name">
              <Input value={me.lastName} onChange={(e) => setMe({ ...me, lastName: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Email" hint="Your sign-in email. Ask a company admin to change it.">
            <Input value={me.email} disabled />
          </FormField>
          <FormField label="Phone" hint="Used for two-step verification by WhatsApp and for marketing test sends.">
            <PhoneField value={me.phone ?? ''} onChange={(e164) => setMe({ ...me, phone: e164 })} placeholder="50 000 0000" />
          </FormField>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title="Password" description="You need your current password to set a new one.">
        <SettingsCard padded={false}>
          <SettingsRow
            icon={<Lock size={16} />}
            label="Password"
            hint="At least 8 characters."
            value={<span className="tracking-widest text-text-muted">••••••••</span>}
            action={<Button variant="secondary" size="sm" onClick={() => setPwOpen(true)}>Change</Button>}
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Two-step verification"
        description="After your password, sign-in also asks for a 6-digit code — from an authenticator app or your WhatsApp."
      >
        <TwoFactorCard phone={me.phone} />
      </SettingsSection>

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Change password">
        <FormField label="Current password" required>
          <Input
            type="password"
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Enter current password"
          />
        </FormField>
        <FormField label="New password" required hint="Minimum 8 characters">
          <Input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password"
            invalid={newPw.length > 0 && newPw.length < 8}
          />
        </FormField>
        <FormField
          label="Confirm new password"
          required
          error={confirmPw.length > 0 && confirmPw !== newPw ? 'Passwords do not match' : undefined}
        >
          <Input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Repeat new password"
            invalid={confirmPw.length > 0 && confirmPw !== newPw}
          />
        </FormField>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setPwOpen(false)}>
            Cancel
          </Button>
          <Button
            loading={busy}
            disabled={!currentPw || newPw.length < 8 || newPw !== confirmPw}
            onClick={changePassword}
          >
            Change password
          </Button>
        </div>
      </Modal>
    </SettingsPage>
  );
}
